/** Static export contracts. Channel tags never choose a placement or canvas. */
export const CREATIVE_LAYOUT_VERSION = '2026-10-27.1' as const;
export interface CreativeAdPlacement {
  id: string;
  version: string;
  platform: 'meta' | 'linkedin' | 'google';
  name: string;
  width: number;
  height: number;
  mimeTypes: readonly ('image/png' | 'image/jpeg')[];
  maximumBytes: number | null;
  verification: 'primary_verified' | 'export_preset_unverified';
  sources: readonly string[];
  checkedAt: string;
  mobileOnly: boolean;
  recommendations: { headlineCharacters: number | null; introductoryCharacters: number | null; note: string };
  technical: { minimumWidth?: number; minimumHeight?: number; maximumWidth?: number; maximumHeight?: number };
  /** App template padding; never a claim of platform-safe zones. */
  contentInset: number;
}
const make = (id: string, platform: CreativeAdPlacement['platform'], name: string,
  width: number, height: number, mobileOnly = false): CreativeAdPlacement => ({
  id, platform, name, width, height, mobileOnly, version: CREATIVE_LAYOUT_VERSION,
  mimeTypes: ['image/png', 'image/jpeg'],
  maximumBytes: platform === 'google' ? 150_000 : platform === 'linkedin' ? 5_000_000 : null,
  verification: platform === 'meta' ? 'export_preset_unverified' : 'primary_verified',
  sources: platform === 'google' ? ['https://support.google.com/google-ads/answer/1722096?hl=en']
    : platform === 'linkedin' ? ['https://business.linkedin.com/advertise/ads/sponsored-content/single-image-ads-specs']
    : ['https://www.facebook.com/business/ads-guide/image/facebook-feed', 'https://www.facebook.com/business/ads-guide/image/instagram-story'],
  checkedAt: '2026-10-27', contentInset: 0.06,
  recommendations: { headlineCharacters: platform === 'linkedin' ? 70 : null, introductoryCharacters: platform === 'linkedin' ? 150 : null, note: platform === 'google' ? 'Uploaded static display; regional availability varies. Keep copy brief and legible.' : platform === 'linkedin' ? 'Copy recommendations describe platform fields, not text inside the image. Portrait delivers on mobile only.' : 'App export preset. Verify current placement specifications before publishing.' },
  technical: platform === 'linkedin' ? width === height ? { minimumWidth: 360, minimumHeight: 360, maximumWidth: 4320, maximumHeight: 4320 } : mobileOnly ? { minimumWidth: 360, minimumHeight: 640, maximumWidth: 2430, maximumHeight: 4320 } : { minimumWidth: 640, minimumHeight: 360, maximumWidth: 7680, maximumHeight: 4320 } : {},
});
export const CREATIVE_AD_PLACEMENTS: readonly CreativeAdPlacement[] = [
  make('meta-feed-square', 'meta', 'Facebook / Instagram · Square feed', 1080, 1080),
  make('meta-feed-portrait', 'meta', 'Facebook / Instagram · Portrait feed', 1440, 1800),
  make('meta-stories', 'meta', 'Facebook / Instagram · Stories', 1440, 2560),
  make('linkedin-square', 'linkedin', 'LinkedIn · Square', 1200, 1200),
  make('linkedin-landscape', 'linkedin', 'LinkedIn · Landscape', 1200, 628),
  make('linkedin-portrait', 'linkedin', 'LinkedIn · Portrait (mobile)', 720, 900, true),
  make('google-small-square', 'google', 'Google Display · Small square', 200, 200),
  make('google-vertical-rectangle', 'google', 'Google Display · Vertical rectangle', 240, 400),
  make('google-square', 'google', 'Google Display · Square', 250, 250),
  make('google-triple-widescreen', 'google', 'Google Display · Triple widescreen', 250, 360),
  make('google-netboard', 'google', 'Google Display · Netboard', 580, 400),
  make('google-skyscraper', 'google', 'Google Display · Skyscraper', 120, 600),
  make('google-wide-skyscraper', 'google', 'Google Display · Wide skyscraper', 160, 600),
  make('google-portrait', 'google', 'Google Display · Portrait', 300, 1050),
  make('google-banner', 'google', 'Google Display · Banner', 468, 60),
  make('google-top-banner', 'google', 'Google Display · Top banner', 930, 180),
  make('google-large-leaderboard', 'google', 'Google Display · Large leaderboard', 970, 90),
  make('google-panorama', 'google', 'Google Display · Panorama', 980, 120),
  make('google-mobile-300', 'google', 'Google Display · Mobile banner 300', 300, 50),
  make('google-medium-rectangle', 'google', 'Google Display · Medium rectangle', 300, 250),
  make('google-large-rectangle', 'google', 'Google Display · Large rectangle', 336, 280),
  make('google-half-page', 'google', 'Google Display · Half page', 300, 600),
  make('google-leaderboard', 'google', 'Google Display · Leaderboard', 728, 90),
  make('google-billboard', 'google', 'Google Display · Billboard', 970, 250),
  make('google-mobile-banner', 'google', 'Google Display · Mobile banner', 320, 50),
  make('google-large-mobile-banner', 'google', 'Google Display · Large mobile banner', 320, 100),
];
export const CREATIVE_AD_FONTS = [
  { id: 'inter', family: 'Ad Inter', asset: '/fonts/inter-400.woff2', version: 'bundled-1', sha256: '8909904ab6c872eb994093482a88a28eca2cd95912d7b6fecd72103b0dc07edc' },
  { id: 'dm-sans', family: 'Ad DM Sans', asset: '/fonts/dm-sans-400.woff2', version: 'bundled-1', sha256: '4ab51eb2cd7305d177187908d6397474d4520663f6c6e572feb0a64f4fa80006' },
  { id: 'montserrat', family: 'Ad Montserrat', asset: '/fonts/montserrat-400.woff2', version: 'bundled-1', sha256: 'e66bcd2761ab6924b25ce70dafe10e57a39193c4fea1516730bd9cb5240af6c8' },
  { id: 'playfair', family: 'Ad Playfair', asset: '/fonts/playfair-display-400.woff2', version: 'bundled-1', sha256: '5d91eb5d522a03081946c44c8ca17c902230dfed5f0f9b5014262135d47b15b2' },
] as const;
export interface CreativeAdComposition {
  schemaVersion: 1;
  placementId: string;
  template: 'split' | 'footer';
  templateVersion: 1;
  source: { token: string; revision: number };
  logo: { token: string; revision: number } | null;
  headline: string;
  body: string;
  cta: string;
  fontId: (typeof CREATIVE_AD_FONTS)[number]['id'];
  background: string;
  foreground: string;
  fit: 'contain' | 'cover';
  mimeType: 'image/png' | 'image/jpeg';
  jpegQuality: number;
  intendedUse: 'internal' | 'external';
}
export interface CreativeAdExportManifest {
  schemaVersion: 1;
  kind: 'local_ad_export';
  composition: CreativeAdComposition;
  placement: CreativeAdPlacement;
  font: (typeof CREATIVE_AD_FONTS)[number];
  output: { width: number; height: number; mimeType: string; bytes: number };
}
